import { Icon } from '@iconify/react';
import { process, State } from '@progress/kendo-data-query';
import { FieldWrapper } from '@progress/kendo-react-form';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { InputPrefix, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useWorkAreaSearchColumns from 'src/assets/static/work_area/useWorkAreaSearchColumns';
import GridWrapper from 'src/components/GridWrapper';
import Button from 'src/components/design-system/Button';
import useAuth from 'src/hooks/useAuth';
import useConfirm from 'src/hooks/useConfirm';
import useWindowSize from 'src/hooks/useWindowSize';
import useCompanyExportTreeApi from 'src/services/work_area/useCompanyExportTreeMutation';
import { downloadFileFromUrl } from 'src/utils/fileTransformation';
import { useContextSelector } from 'use-context-selector';
import { WorkAreaContext, WorkAreaContextProvider } from './WorkAreaContext';
import { resolveWorkAreaSearchExportCompanyId, useWorkAreaCompanyUsersSearch } from './useWorkArea';
import {
  WORK_AREA_MODAL_SEARCH_FIELD_ID,
  WORK_AREA_SEARCH_AUTOCOMPLETE,
  WORK_AREA_SEARCH_FIELD_NAME,
} from './workAreaSearchField';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const initialDataState: State = {
  skip: 0,
  take: 50,
  sort: [],
};

/** 50px inset from top and bottom of the viewport for the search results modal. */
const SEARCH_MODAL_VIEWPORT_PADDING = 100;
/** Title bar, footer actions, search/export toolbar, and content gaps inside the modal. */
const SEARCH_MODAL_CHROME_HEIGHT = 176;
const SEARCH_MODAL_MIN_GRID_HEIGHT = 300;
const PO_SPENDING_LIMIT_FIELD = 'poSpendingLimit';
const PO_SPENDING_LIMIT_SORT_FIELD = 'poSpendingLimitSortValue';

const parsePoSpendingLimit = (value?: string | null): number => {
  if (!value) {
    return Number.NEGATIVE_INFINITY;
  }
  if (value.trim().toLowerCase() === 'unlimited') {
    return Number.POSITIVE_INFINITY;
  }
  const numericPortion = value.replace(/[^0-9.-]/g, '');
  const parsed = Number(numericPortion);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

interface WorkAreaSearchResultsContentProps {
  searchTerm: string;
  onClose?: () => void;
  /** Root company for search (selected in CompanyTree or logged-in company). Passed from Work Area so modal has correct value when rendered outside WorkAreaContext. */
  effectiveRootCompanyId: number | null;
  isSuperAdmin: boolean;
  selectedCompanyId: number | null;
}

const WorkAreaSearchResultsContent = ({
  searchTerm,
  onClose,
  effectiveRootCompanyId,
  isSuperAdmin,
  selectedCompanyId,
}: WorkAreaSearchResultsContentProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { height: windowHeight } = useWindowSize();
  const gridHeight = useMemo(() => {
    if (windowHeight > 0) {
      const available = windowHeight - SEARCH_MODAL_VIEWPORT_PADDING - SEARCH_MODAL_CHROME_HEIGHT;
      return `${Math.max(SEARCH_MODAL_MIN_GRID_HEIGHT, available)}px`;
    }
    return `calc(100vh - ${SEARCH_MODAL_VIEWPORT_PADDING + SEARCH_MODAL_CHROME_HEIGHT}px)`;
  }, [windowHeight]);
  const isImpersonating = useContextSelector(WorkAreaContext, (context) => context.isImpersonating);
  const impersonatedClientId = useContextSelector(
    WorkAreaContext,
    (context) => context.impersonatedClientId
  );
  const isImpersonatedClient = useContextSelector(
    WorkAreaContext,
    (context) => context.isImpersonatedClient
  );

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [search, setSearch] = useState(searchTerm);
  const [queryText, setQueryText] = useState(searchTerm);
  const {
    data: searchResponse,
    isLoading,
    isFetching,
  } = useWorkAreaCompanyUsersSearch(queryText, effectiveRootCompanyId);
  const isGridLoading = isLoading || isFetching;
  const rows = useMemo(
    () =>
      searchResponse.map((item) => ({
        ...item,
        rowId: `${item.parentCompanyId}-${item.clientId}`,
        [PO_SPENDING_LIMIT_SORT_FIELD]: parsePoSpendingLimit(item.poSpendingLimit),
      })),
    [searchResponse]
  );
  const columns = useWorkAreaSearchColumns(onClose);

  const applySearch = useCallback((value: string) => {
    setQueryText(value);
    setDataState((prev) => ({ ...prev, skip: 0 }));
  }, []);

  // Immediate search when modal opens or searchTerm prop changes.
  useEffect(() => {
    setSearch(searchTerm);
    applySearch(searchTerm);
  }, [applySearch, searchTerm]);

  // Debounced search when user types in the modal input.
  useEffect(() => {
    if (search === queryText) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      applySearch(search);
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [applySearch, queryText, search]);

  const processed = useMemo(() => {
    const normalizedDataState: State = {
      ...dataState,
      sort:
        dataState.sort?.map((descriptor) => ({
          ...descriptor,
          field:
            descriptor.field === PO_SPENDING_LIMIT_FIELD
              ? PO_SPENDING_LIMIT_SORT_FIELD
              : descriptor.field,
        })) ?? [],
    };
    return process(rows, normalizedDataState);
  }, [rows, dataState]);

  const onDataStateChange = useCallback((event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  }, []);

  const exportCompanyId = useMemo(
    () => resolveWorkAreaSearchExportCompanyId(effectiveRootCompanyId, isSuperAdmin, user),
    [effectiveRootCompanyId, isSuperAdmin, user]
  );

  const { exportExecute, exportLoading } = useCompanyExportTreeApi();
  const handleExportToExcel = useCallback(async () => {
    const trimmedSearch = queryText.trim();
    if (exportCompanyId == null || !trimmedSearch) {
      return;
    }
    try {
      const res = await exportExecute({
        companyId: exportCompanyId,
        searchTerm: trimmedSearch,
      });
      if (!res?.data?.downloadUrl) {
        return;
      }
      await downloadFileFromUrl(res.data.downloadUrl, 'SearchResults_Users.xls');
      toast.success(t('ediExportLogsSuccess'));
    } catch {
      toast.error(t('ediExportLogsError'));
    }
  }, [exportExecute, exportCompanyId, queryText, t]);

  const rowRender = useCallback(
    (tr: React.ReactElement, props: { dataItem?: { clientId?: number } }) => {
      const row = props.dataItem;
      const className = [
        tr.props.className,
        row && isImpersonatedClient(row.clientId) ? 'work-area-search-row-impersonated' : '',
      ]
        .filter(Boolean)
        .join(' ');
      return React.cloneElement(tr, { className });
    },
    [isImpersonatedClient]
  );

  useEffect(() => {
    if (!isImpersonating || impersonatedClientId == null || isGridLoading || !rows.length) {
      return;
    }

    const index = rows.findIndex((row) => row.clientId === impersonatedClientId);
    if (index < 0) {
      return;
    }

    const take = dataState.take ?? 50;
    const targetSkip = Math.floor(index / take) * take;

    if (dataState.skip !== targetSkip) {
      setDataState((prev) => ({ ...prev, skip: targetSkip }));
      return;
    }

    requestAnimationFrame(() => {
      document
        .getElementById('work-area-search-results-grid')
        ?.querySelector('.work-area-search-row-impersonated')
        ?.scrollIntoView({ block: 'nearest' });
    });
  }, [dataState.skip, dataState.take, impersonatedClientId, isGridLoading, isImpersonating, rows]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex-[1_1_250px] min-w-[200px] max-w-[350px]">
          <FieldWrapper className="flex flex-row space-x-2">
            <Label
              editorId={WORK_AREA_MODAL_SEARCH_FIELD_ID}
              className="text-xs flex items-center whitespace-nowrap"
            >
              {t('searchBy')}
            </Label>
            <TextBox
              id={WORK_AREA_MODAL_SEARCH_FIELD_ID}
              name={WORK_AREA_SEARCH_FIELD_NAME}
              max={500}
              maxLength={500}
              size="medium"
              style={{
                fontSize: '14px',
              }}
              placeholder={t(
                !isSuperAdmin
                  ? 'searchByContactName'
                  : selectedCompanyId != null
                  ? 'searchByContactNameInSelectedCompany'
                  : 'searchByCompanyOrContactName'
              )}
              width="100%"
              value={search}
              autoComplete={WORK_AREA_SEARCH_AUTOCOMPLETE}
              onChange={(event: TextBoxChangeEvent): void => {
                setSearch(String(event.value ?? ''));
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applySearch(search);
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  onClose?.();
                }
              }}
              prefix={() => (
                <InputPrefix className="pl-2">
                  <Icon height={'18'} width={'18'} color="#AAAAAA" icon={'lucide:search'} />
                </InputPrefix>
              )}
            />
          </FieldWrapper>
        </div>
        <Button
          color="info"
          size="md"
          disabled={
            exportLoading ||
            exportCompanyId == null ||
            !queryText.trim() ||
            processed.data.length === 0
          }
          loading={exportLoading}
          onClick={() => void handleExportToExcel()}
        >
          {t('exportToExcel')}
        </Button>
      </div>
      <GridWrapper
        id="work-area-search-results-grid"
        className="min-h-0 flex-1"
        tableTextId={t('users')}
        DataItemKey="rowId"
        sortable
        resizable
        columns={columns}
        total={processed.total}
        dataSource={processed.data}
        dataState={dataState}
        onDataStateChange={onDataStateChange}
        pageable={gridPagerSettings}
        height={gridHeight}
        noRecordText={t('noResultsFound')}
        loading={isGridLoading}
        rowRender={rowRender}
      />
    </div>
  );
};

export interface OpenSearchResultsParams {
  effectiveRootCompanyId: number | null;
  isSuperAdmin: boolean;
  selectedCompanyId: number | null;
  selectedCompanyName: string | null;
}

export const useWorkAreaSearchResultsModal = (searchTerm: string) => {
  const { t } = useTranslation();
  const { confirm, onCancel } = useConfirm();

  return useCallback(
    (params: OpenSearchResultsParams) => {
      const { effectiveRootCompanyId, isSuperAdmin, selectedCompanyId, selectedCompanyName } =
        params;
      const title =
        isSuperAdmin && selectedCompanyId == null
          ? t('searchResultsAllCompanies', 'Search Results - All Companies')
          : selectedCompanyName
          ? `${t('searchResults', 'Search Results')} - ${selectedCompanyName}`
          : t('searchResults', 'Search Results');
      confirm({
        title,
        content: (
          <WorkAreaContextProvider>
            <WorkAreaSearchResultsContent
              searchTerm={searchTerm}
              onClose={onCancel}
              effectiveRootCompanyId={effectiveRootCompanyId}
              isSuperAdmin={isSuperAdmin}
              selectedCompanyId={selectedCompanyId}
            />
          </WorkAreaContextProvider>
        ),
        cancelButtonText: t('Close'),
        cancelColor: 'info',
        hasNoConfirmButton: true,
        width: '90%',
        height: `calc(100vh - ${SEARCH_MODAL_VIEWPORT_PADDING}px)`,
        className:
          'work-area-search-results-modal [&_.k-widget.k-window.k-dialog]:!max-h-[calc(100vh-100px)] [&_.k-dialog-content]:!flex [&_.k-dialog-content]:!min-h-0 [&_.k-dialog-content]:!flex-1 [&_.k-dialog-content]:!flex-col',
      });
    },
    [confirm, onCancel, searchTerm, t]
  );
};
